// src/components/dashboard/ai/DashboardAiHelper.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — dashboard guide
//   <DashboardAiHelper isOpen onClose … />  drawer: Modules · AI engines · Widgets · How-to
//   <WidgetHelpButton widgetKey="risk" />    small "?" on every widget (what it shows, how to act)
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (road AI / ML pass)
//   • New "Road AI" tab (index ROAD_AI_TAB_INDEX, appended so existing defaultIndex values
//     still open the same tabs): live road network numbers from brief.breakdown.roads
//     (GET /roads/ai-summary), the worst roads with "Open report" deep links
//     (/admin/road-works?tab=report&road=<id>), and every road model with its scoring rules
//     and offline fallback (ROAD_AI_MODELS — matches ml/model.py road-pci-1.1 / road-qa-1.1).
//   • AI engines tab shows each engine's detail (Ollama model, ML circuit) and which road
//     models the ML service runs / what falls back to rules when it is offline.
//   • How-to: Road Works Report, segment evaluation, QA audit statuses, inspection checklists.
// --- END UPDATE
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
    Tabs, TabList, Tab, TabPanels, TabPanel, Accordion, AccordionItem, AccordionButton,
    AccordionPanel, AccordionIcon, Box, Text, VStack, HStack, Badge, Button, Tag, Wrap, WrapItem,
    Icon, IconButton, Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody,
    PopoverHeader, Portal, useColorModeValue,
    SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Alert, AlertIcon, // --- UPDATE: Road AI tab
} from '@chakra-ui/react';
import { MdHelpOutline, MdArrowForward, MdAutoAwesome, MdAdd } from 'react-icons/md';
import { FaRoad } from 'react-icons/fa'; // --- UPDATE
import { useNavigate } from 'react-router-dom';
import { FLOWSENSE_MODULES, MODULE_KEYS, AI_ENGINES, ENGINE_KEYS, WIDGET_GUIDE } from './flowsenseModules';
import { useFlowSenseIntel } from './FlowSenseIntelContext';
import { ROAD_AI_MODELS } from './flowsenseIntel'; // --- UPDATE

const ENGINE_COLOR = { online: 'green', offline: 'red', unknown: 'gray' };
// --- UPDATE: Road AI tab
export const ROAD_AI_TAB_INDEX = 4;
const ROAD_REPORT_ROUTE = `${FLOWSENSE_MODULES.road_works?.route || '/admin/road-works'}?tab=report`;
const roadReportFor = (id) => `${ROAD_REPORT_ROUTE}&road=${encodeURIComponent(id)}`;
const pciColor = (v) => (v == null ? 'gray' : v < 50 ? 'red' : v < 70 ? 'orange' : v < 85 ? 'yellow' : 'green');
const ML_ROAD_NOTE = 'Road models on this service: segment PCI (road-pci-1.1), paving QA audit (road-qa-1.1), pothole photo verification (road-damage YOLO, needs models/best.pt). When it is offline, PCI and QA use the identical FlowSense rules; photo verification is unavailable.';
// --- END UPDATE

const HOW_TO = [
    { q: 'What does the FlowSense AI score mean?', a: 'A 0–100 operational score. It starts at 100 and loses points for SLA breaches, unassigned critical jobs, stalled jobs, sensor alerts, assets predicted to fail, critical stock, engines offline and low SLA compliance. Hover the score to see the exact deductions.' },
    { q: 'Where do the AI answers come from?', a: 'Questions go to the Ollama model on the FlowSense server together with a live snapshot (counts and titles you can already see). If Ollama is offline, FlowSense answers from its built-in rules and labels the answer "FlowSense rules".' },
    { q: 'How do I create a work order or inspection?', a: 'From Work Orders ("Create New" / "New Inspection"), or from an alert, issue, asset, road, well or sensor — the form opens pre-filled. Choose the domain, type and category, link the record, describe the work; the form\'s AI assistant checks each step.' },
    { q: 'What does "Live" mean?', a: 'The dashboard listens to the FlowSense WebSocket: work order changes, SLA watchdog scans, sensor anomalies, AI predictions, road and asset updates refresh the widgets within seconds. Changes made in this browser are shared between open pages too. If the badge says Offline, widgets fall back to polling.' },
    { q: 'How do I customise the dashboard?', a: 'Unlock the layout (padlock), drag and resize widgets, add more from "Add Widget", then Save (or turn on Auto-save).' },
    // --- UPDATE: road AI / ML
    { q: 'How do I use the Road Works Report?', a: 'Road Works → "Road Works Report" tab. The table lists every road on the inventory page with its segments, ML segment PCI, AI remaining life, AI priority and next action, work orders, QA and inspections. Click a road to open its full report below: FlowSense AI, segments, logistics, work orders with QA audit and tick-box inspections.' },
    { q: 'How is a road segment scored (PCI)?', a: 'Each 50/100 m segment starts at 100: −12 per pothole (max 45), cracking −5 / −15 / −28 / −40 (low → alligator), −1.8 per mm of rutting (max 30), −12 for a flat cross-fall. 85+ routine maintenance, 70–84 crack seal, 50–69 mill & overlay, below 50 reconstruction. Run it with "Evaluate segments" in the road report; if the ML service is offline the same rules are applied.' },
    { q: 'What do the paving QA results mean?', a: 'The AI audit scores a finished paving job from 100 using compaction, smoothness (IRI), material yield, asphalt temperatures and the acceptance checklist. 85+ with no failed critical checklist item is Approved; 65–84 is Flagged for penalty (10% payment retention, 2-year warranty extension); below 65 is Rework mandated at the contractor\'s cost.' },
    { q: 'How do road inspections work?', a: 'In the road report choose "New inspection": tick every checklist item OK / Defect / N/A, add measurements and notes (required for defects), rate the job and submit. Submitting records a condition assessment, can raise a follow-up work order automatically and feeds the QA audit and the AI priority.' },
    // --- END UPDATE
];

/** Small "?" button that explains one widget. */
export const WidgetHelpButton = memo(({ widgetKey, ...rest }) => {
    const g = WIDGET_GUIDE[widgetKey];
    if (!g) return null;
    return (
        <Popover placement="bottom-end" isLazy>
            <PopoverTrigger>
                <IconButton size="xs" variant="ghost" icon={<Icon as={MdHelpOutline} />} aria-label={`What is ${g.name}?`} borderRadius="full" {...rest} />
            </PopoverTrigger>
            <Portal>
                <PopoverContent fontSize="sm" maxW="300px" zIndex={1500}>
                    <PopoverArrow />
                    <PopoverHeader fontWeight="800">{g.name}</PopoverHeader>
                    <PopoverBody>
                        <Text mb={2}>{g.shows}</Text>
                        <Text fontSize="xs" color="gray.500"><b>Data:</b> {g.source}</Text>
                        <Text fontSize="xs" color="gray.500"><b>What to do:</b> {g.act}</Text>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
});
WidgetHelpButton.displayName = 'WidgetHelpButton';
WidgetHelpButton.propTypes = { widgetKey: PropTypes.string.isRequired };

// --- UPDATE: Road AI tab body
const RoadAiPanel = memo(({ roads, cardBg, muted, onOpen }) => {
    const r = roads || null;
    return (
        <VStack align="stretch" spacing={3}>
            {!r?.loaded ? (
                <Alert status="info" borderRadius="lg" fontSize="sm">
                    <AlertIcon />
                    Road network AI data isn&apos;t loaded yet{r ? ` — ${r.openJobs} open road job(s) from the work order list` : ''}.
                </Alert>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">Segments</StatLabel>
                            <StatNumber fontSize="lg">{r.segments}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>{r.segmented}/{r.total} roads sliced</StatHelpText>
                        </Stat>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">Segment PCI</StatLabel>
                            <StatNumber fontSize="lg" color={`${pciColor(r.avgPci)}.500`}>{r.avgPci ?? '—'}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>{r.poorSegments} &lt; 50 · {r.criticalSegments} &lt; 35</StatHelpText>
                        </Stat>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">ML coverage</StatLabel>
                            <StatNumber fontSize="lg">{r.mlCoverage != null ? `${r.mlCoverage}%` : '—'}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>{r.mlEvaluated} roads evaluated{r.unscoredSegments ? ` · ${r.unscoredSegments} unscored` : ''}</StatHelpText>
                        </Stat>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">Road jobs</StatLabel>
                            <StatNumber fontSize="lg">{r.openJobs}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>{r.criticalJobs} critical · {r.overdueJobs} overdue</StatHelpText>
                        </Stat>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">Paving QA (30 d)</StatLabel>
                            <StatNumber fontSize="lg">{r.qa30.audits}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>{r.qa30.approved} ok · {r.qa30.flagged} flagged · {r.qa30.rework} rework · {r.qaPending} pending</StatHelpText>
                        </Stat>
                        <Stat bg={cardBg} borderRadius="lg" p={2.5}>
                            <StatLabel fontSize="2xs">Inspections</StatLabel>
                            <StatNumber fontSize="lg">{r.inspections30.submitted}</StatNumber>
                            <StatHelpText fontSize="2xs" mb={0}>30 d{r.inspections30.avgScore != null ? ` · avg ${r.inspections30.avgScore}` : ''} · {r.inspectionsOverdue} overdue</StatHelpText>
                        </Stat>
                    </SimpleGrid>
                    {r.rulScored > 0 && (
                        <Text fontSize="xs" color={muted}>Remaining life: <b>{r.rulCritical}</b> road(s) ≤ 2 years, <b>{r.rulPoor}</b> within 5 years ({r.rulScored} scored by the model).</Text>
                    )}
                    {r.worst.length > 0 && (
                        <Box>
                            <Text fontWeight="700" fontSize="sm" mb={1}>Worst roads (segment PCI)</Text>
                            <VStack align="stretch" spacing={1}>
                                {r.worst.map(w => (
                                    <HStack key={w.id} bg={cardBg} borderRadius="lg" px={3} py={2} justify="space-between">
                                        <Box minW={0}>
                                            <Text fontSize="sm" fontWeight="700" noOfLines={1}>{w.name}</Text>
                                            <Text fontSize="2xs" color={muted}>min {w.minPci ?? '—'} · {w.poorSegments} poor segment(s) · {w.potholes} potholes</Text>
                                        </Box>
                                        <HStack spacing={2}>
                                            <Badge colorScheme={pciColor(w.avgPci)}>PCI {w.avgPci ?? '—'}</Badge>
                                            <Button size="xs" variant="outline" onClick={() => onOpen(roadReportFor(w.id))} aria-label={`Open report for ${w.name}`}>Open report</Button>
                                        </HStack>
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    )}
                    {r.warnings.length > 0 && <Text fontSize="2xs" color={muted}>Partial data: {r.warnings.join(', ')}.</Text>}
                </>
            )}
            <Button size="sm" colorScheme="yellow" leftIcon={<Icon as={FaRoad} />} onClick={() => onOpen(ROAD_REPORT_ROUTE)}>Open Road Works Report</Button>
            <Text fontWeight="700" fontSize="sm" mt={1}>Road models</Text>
            <Accordion allowToggle>
                {ROAD_AI_MODELS.map(m => (
                    <AccordionItem key={m.key}>
                        <AccordionButton>
                            <Box flex="1" textAlign="left">
                                <Text fontWeight="700" fontSize="sm">{m.name}</Text>
                                <Text fontSize="2xs" color={muted}>{m.version}</Text>
                            </Box>
                            <Badge colorScheme={m.engine === 'ml' ? 'purple' : 'gray'} mr={2} fontSize="2xs">{m.engine === 'ml' ? 'ML service' : 'rules'}</Badge>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel fontSize="sm">
                            <Text mb={2}>{m.explain}</Text>
                            <VStack align="stretch" spacing={0.5} mb={2}>{m.rules.map(x => <Text key={x} fontSize="xs">• {x}</Text>)}</VStack>
                            <Text fontSize="xs" color={muted}><b>Offline:</b> {m.fallback}</Text>
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </VStack>
    );
});
RoadAiPanel.displayName = 'RoadAiPanel';
RoadAiPanel.propTypes = { roads: PropTypes.object, cardBg: PropTypes.string, muted: PropTypes.string, onOpen: PropTypes.func.isRequired };
// --- END UPDATE

function DashboardAiHelper({ isOpen, onClose, visibleWidgetKeys = [], onAddWidget, onOpenAi, defaultIndex = 0 }) {
    const navigate = useNavigate();
    const { brief } = useFlowSenseIntel();
    const muted = useColorModeValue('gray.600', 'gray.400');
    const cardBg = useColorModeValue('gray.50', 'gray.700');
    const engines = brief?.breakdown?.engines || ENGINE_KEYS.map(k => ({ key: k, status: 'unknown' }));

    return (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">
                    <HStack spacing={2}>
                        <Icon as={MdAutoAwesome} color="purple.500" />
                        <Text>FlowSense guide</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="normal" color={muted}>How the system fits together and how the AI works</Text>
                </DrawerHeader>
                <DrawerBody>
                    {onOpenAi && (
                        <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdAutoAwesome} />} mb={3} onClick={() => { onClose(); onOpenAi(); }} w="100%">
                            Open FlowSense AI
                        </Button>
                    )}
                    <Tabs size="sm" colorScheme="purple" defaultIndex={defaultIndex} isLazy>
                        <TabList flexWrap="wrap">
                            <Tab>Modules</Tab>
                            <Tab>AI engines</Tab>
                            <Tab>Widgets</Tab>
                            <Tab>How to</Tab>
                            <Tab><Icon as={FaRoad} mr={1} /> Road AI</Tab>{/* --- UPDATE */}
                        </TabList>
                        <TabPanels>
                            <TabPanel px={0}>
                                <Accordion allowToggle>
                                    {MODULE_KEYS.map((k) => {
                                        const m = FLOWSENSE_MODULES[k];
                                        const st = brief?.modules?.[k];
                                        return (
                                            <AccordionItem key={k}>
                                                <AccordionButton>
                                                    <Box flex="1" textAlign="left">
                                                        <Text fontWeight="700" fontSize="sm">{m.name}</Text>
                                                        {st?.metric && <Text fontSize="xs" color={muted}>{st.metric}</Text>}
                                                    </Box>
                                                    {st && <Badge colorScheme={st.status === 'attention' ? 'orange' : st.status === 'ok' ? 'green' : 'gray'} mr={2} fontSize="2xs">{st.status}</Badge>}
                                                    <AccordionIcon />
                                                </AccordionButton>
                                                <AccordionPanel fontSize="sm">
                                                    <Text mb={2}>{m.summary}</Text>
                                                    <Text fontWeight="700" fontSize="xs">What you can do there</Text>
                                                    <VStack align="stretch" spacing={0.5} mb={2}>{m.doHere.map(d => <Text key={d} fontSize="xs">• {d}</Text>)}</VStack>
                                                    <Text fontWeight="700" fontSize="xs">AI behind it</Text>
                                                    <Wrap spacing={1} mb={2}>{m.aiModels.map(a => <WrapItem key={a}><Tag size="sm" colorScheme="purple">{a}</Tag></WrapItem>)}</Wrap>
                                                    <Text fontSize="xs" color={muted} mb={2}><b>Feeds:</b> {m.feeds}</Text>
                                                    {m.route && <Button size="xs" colorScheme={m.color} rightIcon={<Icon as={MdArrowForward} />} onClick={() => { onClose(); navigate(m.route); }}>Open {m.name}</Button>}
                                                </AccordionPanel>
                                            </AccordionItem>
                                        );
                                    })}
                                </Accordion>
                            </TabPanel>
                            <TabPanel px={0}>
                                <VStack align="stretch" spacing={2}>
                                    {engines.map((e) => {
                                        const info = AI_ENGINES[e.key];
                                        return (
                                            <Box key={e.key} bg={cardBg} borderRadius="lg" p={3}>
                                                <HStack justify="space-between">
                                                    <Text fontWeight="700" fontSize="sm">{info.name}</Text>
                                                    <Badge colorScheme={ENGINE_COLOR[e.status]}>{e.status}</Badge>
                                                </HStack>
                                                <Text fontSize="xs" color={muted} mt={1}>{info.explain}</Text>
                                                <Text fontSize="xs" mt={1}><b>Powers:</b> {info.powers.join(', ')}</Text>
                                                {/* --- UPDATE */}
                                                {e.detail && <Text fontSize="xs" color={muted} mt={1}><b>Now:</b> {e.detail}</Text>}
                                                {e.key === 'ml' && <Text fontSize="xs" mt={1}><b>Roads:</b> {ML_ROAD_NOTE}</Text>}
                                                {/* --- END UPDATE */}
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </TabPanel>
                            <TabPanel px={0}>
                                <VStack align="stretch" spacing={2}>
                                    {Object.entries(WIDGET_GUIDE).map(([key, g]) => {
                                        const on = visibleWidgetKeys.includes(key);
                                        return (
                                            <Box key={key} bg={cardBg} borderRadius="lg" p={3}>
                                                <HStack justify="space-between">
                                                    <Text fontWeight="700" fontSize="sm">{g.name}</Text>
                                                    {on ? <Badge colorScheme="green">On dashboard</Badge> : onAddWidget && (
                                                        <Button size="xs" leftIcon={<Icon as={MdAdd} />} onClick={() => onAddWidget(key)} aria-label={`Add ${g.name}`}>Add</Button>
                                                    )}
                                                </HStack>
                                                <Text fontSize="xs" mt={1}>{g.shows}</Text>
                                                <Text fontSize="xs" color={muted} mt={1}><b>What to do:</b> {g.act}</Text>
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </TabPanel>
                            <TabPanel px={0}>
                                <Accordion allowToggle>
                                    {HOW_TO.map(h => (
                                        <AccordionItem key={h.q}>
                                            <AccordionButton><Box flex="1" textAlign="left" fontWeight="700" fontSize="sm">{h.q}</Box><AccordionIcon /></AccordionButton>
                                            <AccordionPanel fontSize="sm" color={muted}>{h.a}</AccordionPanel>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </TabPanel>
                            {/* --- UPDATE: Road AI tab */}
                            <TabPanel px={0}>
                                <RoadAiPanel roads={brief?.breakdown?.roads} cardBg={cardBg} muted={muted}
                                    onOpen={(route) => { onClose(); navigate(route); }} />
                            </TabPanel>
                            {/* --- END UPDATE */}
                        </TabPanels>
                    </Tabs>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

DashboardAiHelper.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    visibleWidgetKeys: PropTypes.array,
    onAddWidget: PropTypes.func,
    onOpenAi: PropTypes.func,
    defaultIndex: PropTypes.number,
};

export default DashboardAiHelper;
